'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Combobox, type ComboboxOption } from '@leanin/ui';
import { getApiClient } from '../../lib/api-client';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

/** Structured place payload accepted by every server write path that used to
 *  take a free-text location field. Mirrors `PlacePayloadSchema` in
 *  `@leanin/shared/views/place`. */
export type PlaceSelection = {
  placeId: string;
  placeText: string;
  placeLat: number;
  placeLng: number;
  placeCountryCode: string | null;
};

/** Mode controls how Google Places autocomplete is biased.
 *  - `city`  — restrict to localities (city/region/country).
 *  - `venue` — allow POIs, addresses, and cities. */
export type PlacePickerMode = 'city' | 'venue';

export type PlacePickerProps = {
  mode: PlacePickerMode;
  value: PlaceSelection | null;
  onChange: (next: PlaceSelection | null) => void;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  invalid?: boolean;
};

function newSessionToken(): string {
  // crypto.randomUUID is universally available in modern browsers; we still
  // guard for the rare older surface (Vitest jsdom without polyfill, etc.).
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Falls back to a syntactically-valid v4 UUID. Good enough — the server
  // only validates the shape via z.string().uuid().
  const r = () => Math.floor(Math.random() * 0xffff).toString(16).padStart(4, '0');
  return `${r()}${r()}-${r()}-4${r().slice(1)}-${r()}-${r()}${r()}${r()}`;
}

/**
 * Single-select place autocomplete backed by `/api/v1/geo/places/*`. Replaces
 * the legacy OSM-Nominatim `LocationPicker`. The Combobox primitive owns the
 * UI shell; this component handles the autocomplete-debounce, session token
 * lifecycle, and the details fetch on commit.
 *
 * Session token contract (Google Places billing): one token is reused across
 * every keystroke of a single "search session"; when the user commits an
 * option we fire the details fetch with that same token so Google bills the
 * keystroke run + final lookup as one autocomplete session. A fresh token is
 * minted for the next search.
 */
export function PlacePicker({
  mode,
  value,
  onChange,
  placeholder,
  disabled = false,
  ariaLabel,
  invalid = false,
}: PlacePickerProps) {
  // The combobox stores its committed value as the `placeId` (option.value);
  // the visible label comes from the option's `label` (== placeText).
  const [options, setOptions] = useState<ComboboxOption[]>(() =>
    value === null
      ? []
      : [{ value: value.placeId, label: value.placeText }],
  );
  const [loading, setLoading] = useState(false);

  // Active session token. Reset to a fresh UUID after each successful commit
  // (see `commitSelection`) and on initial mount.
  const sessionTokenRef = useRef<string>(newSessionToken());

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cache of `placeId → PlaceSelection` for results pulled this session.
  // Keeps the details fetch idempotent when the user clicks the same row
  // twice (and lets us short-circuit re-hydration of an already-resolved
  // selection that's still in the option list).
  const resolvedRef = useRef<Map<string, PlaceSelection>>(new Map());

  useEffect(() => {
    // Keep the option list in sync with the parent-controlled value so a
    // programmatic clear (`onChange(null)`) and a fresh non-null hydration
    // both reflect in the combobox display.
    if (value === null) {
      setOptions([]);
      return;
    }
    setOptions((prev) => {
      if (prev.some((o) => o.value === value.placeId)) return prev;
      return [{ value: value.placeId, label: value.placeText }];
    });
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const handleQueryChange = useCallback(
    (query: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();

      const trimmed = query.trim();
      if (trimmed.length < MIN_QUERY_LEN) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      timerRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortRef.current = controller;
        try {
          const client = getApiClient();
          const { data, error } = await client.POST(
            '/api/v1/geo/places/autocomplete',
            {
              body: {
                q: trimmed,
                types: mode,
                sessionToken: sessionTokenRef.current,
              },
              signal: controller.signal,
            },
          );
          if (controller.signal.aborted) return;
          if (error !== undefined || data === undefined) {
            setOptions([]);
            return;
          }
          const opts: ComboboxOption[] = data.results.map((r) => ({
            value: r.placeId,
            label: r.primaryText,
            sublabel: r.secondaryText,
          }));
          setOptions(opts);
        } catch (err) {
          if ((err as Error).name === 'AbortError') return;
          setOptions([]);
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      }, DEBOUNCE_MS);
    },
    [mode],
  );

  const commitSelection = useCallback(
    async (placeId: string) => {
      const cached = resolvedRef.current.get(placeId);
      if (cached !== undefined) {
        onChange(cached);
        sessionTokenRef.current = newSessionToken();
        return;
      }
      const client = getApiClient();
      const { data, error } = await client.GET(
        '/api/v1/geo/places/{placeId}',
        {
          params: {
            path: { placeId },
            query: { sessionToken: sessionTokenRef.current },
          },
        },
      );
      if (error !== undefined || data === undefined) {
        // Surfacing a thrown error here would break the combobox's commit
        // flow (the value would stay committed-but-unresolved). Leave the
        // selection untouched and let the user re-pick.
        sessionTokenRef.current = newSessionToken();
        return;
      }
      const resolved: PlaceSelection = {
        placeId: data.placeId,
        placeText: data.text,
        placeLat: data.lat,
        placeLng: data.lng,
        placeCountryCode: data.countryCode,
      };
      resolvedRef.current.set(placeId, resolved);
      // Also refresh the visible label so a commit-from-suggestion shows the
      // formatted text the server resolved (e.g. "Boston, Massachusetts,
      // USA" rather than the suggestion's primaryText "Boston").
      setOptions((prev) =>
        prev.map((o) =>
          o.value === placeId ? { ...o, label: resolved.placeText } : o,
        ),
      );
      onChange(resolved);
      sessionTokenRef.current = newSessionToken();
    },
    [onChange],
  );

  const handleChange = useCallback(
    (next: string | null) => {
      if (next === null) {
        onChange(null);
        // Clearing also ends the current session — a re-pick is a fresh one.
        sessionTokenRef.current = newSessionToken();
        return;
      }
      // Combobox emits the option's `value` (== placeId). Resolve via the
      // details endpoint, then push the structured selection up.
      void commitSelection(next);
    },
    [commitSelection, onChange],
  );

  return (
    <Combobox
      options={options}
      value={value === null ? null : value.placeId}
      onChange={handleChange}
      onQueryChange={handleQueryChange}
      noInternalFilter
      loading={loading}
      placeholder={placeholder ?? (mode === 'city' ? 'Search a city…' : 'Search a place…')}
      emptyLabel="No matches"
      noOptionsLabel="Type to search places…"
      disabled={disabled}
      invalid={invalid}
      ariaLabel={ariaLabel ?? 'Place'}
    />
  );
}
